/** @shell standard @header bar */
import React, { useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useAuthNavigation } from '../shared/navigation/useAuthNavigation';
import { useRequireOnlineOrdersEnabled } from '../shared/navigation/useFeatureGateExit';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import {
  createStackedFooterPrimaryLabelStyle,
  createStackedFooterPrimarySurfaceStyle,
  createStackedFooterSecondaryLabelStyle,
  createStackedFooterSecondarySurfaceStyle,
} from '../shared/theme/sharedStyles';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from '../shared/i18n';
import { clearBookMealData } from '../data/domains/orders';
import { formatCurrency } from '../shared/utils/currencyUtils';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  studioCardShell,
  DASH_STUDIO_RADIUS,
} from '../shared/theme/studio/stackScreenShell';

function createOrderConfirmedStyles(c: ThemeColors) {
  return StyleSheet.create({
    content: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingVertical: appTheme.spacing.xl,
    },
    iconContainer: {
      marginBottom: appTheme.spacing.lg,
    },
    title: {
      ...appTheme.typography.h1,
      color: c.text,
      marginBottom: appTheme.spacing.sm,
    },
    subtitle: {
      ...appTheme.typography.body,
      color: c.textSecondary,
      textAlign: 'center',
      marginBottom: appTheme.spacing.xl,
    },
    orderInfo: {
      alignItems: 'center',
      marginBottom: appTheme.spacing.lg,
    },
    orderLabel: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    orderNumber: {
      ...appTheme.typography.h2,
      color: c.primary,
    },
    totalCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      paddingVertical: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.xl,
      alignItems: 'center',
      marginBottom: appTheme.spacing.lg,
      alignSelf: 'stretch',
    },
    totalLabel: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
    },
    totalValue: {
      ...appTheme.typography.h2,
      color: c.success,
    },
    alertCard: {
      flexDirection: 'row',
      alignItems: 'center',
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.md,
      gap: appTheme.spacing.md,
      alignSelf: 'stretch',
    },
    alertText: {
      flex: 1,
    },
    alertTitle: {
      ...appTheme.typography.body,
      color: c.text,
      fontWeight: '600',
    },
    alertSubtitle: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      marginTop: 2,
    },
    footer: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
      alignSelf: 'stretch',
      width: '100%',
    },
    footerBtnClickable: { alignSelf: 'stretch' },
    footerBtnInner: { alignSelf: 'stretch' },
  });
}

export const OrderConfirmedScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const online_orders_enabled = useRequireOnlineOrdersEnabled();
  const route = useRoute();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createOrderConfirmedStyles(themeColors), [themeColors]);
  const { scrollY } = useScreenHeaderScroll();
  const primary_cta = useMemo(
    () => ({
      surface: createStackedFooterPrimarySurfaceStyle(themeColors),
      label: createStackedFooterPrimaryLabelStyle(themeColors),
    }),
    [themeColors],
  );
  const secondary_cta = useMemo(
    () => ({
      surface: createStackedFooterSecondarySurfaceStyle(themeColors),
      label: createStackedFooterSecondaryLabelStyle(themeColors),
    }),
    [themeColors],
  );
  const params = (route.params as any) || {};
  const { orderNumber, total } = params;

  useEffect(() => {
    clearBookMealData();
  }, []);

  const go_back = useCallback(() => navigation.navigate('MainTabs'), [navigation]);

  if (!online_orders_enabled) {
    return null;
  }

  return (
    <StackScreenShell colors={themeColors}>
      <ScreenHeader title={t('order.confirmedTitle')} onBack={go_back} scrollY={scrollY} />
      <View style={styles.content}>
        <View style={styles.iconContainer}>
          <SvgIcon name="check" size={100} color={themeColors.success} />
        </View>

        <Text style={styles.title}>{t('order.confirmedTitle')}</Text>
        <Text style={styles.subtitle}>{t('order.confirmedMessage')}</Text>

        {orderNumber != null && orderNumber !== '' ? (
          <View style={styles.orderInfo}>
            <Text style={styles.orderLabel}>{t('order.orderNumberLabel')}</Text>
            <Text style={styles.orderNumber}>#{orderNumber}</Text>
          </View>
        ) : null}

        {total != null && total !== '' ? (
          <View style={styles.totalCard}>
            <Text style={styles.totalLabel}>{t('order.totalPaid')}</Text>
            <Text style={styles.totalValue}>{formatCurrency(Number(total) || 0)}</Text>
          </View>
        ) : null}

        <View style={styles.alertCard}>
          <SvgIcon name="time" size={24} color={themeColors.primary} />
          <View style={styles.alertText}>
            <Text style={styles.alertTitle}>{t('order.estimatedDelivery')}</Text>
            <Text style={styles.alertSubtitle}>{t('order.estimatedDeliverySubtitle')}</Text>
          </View>
        </View>
      </View>

      <View style={[styles.footer, { paddingBottom: Math.max(insets.bottom, appTheme.spacing.md) }]}>
        <Clickable
          onPress={() => navigation.navigate('OrderHistory')}
          style={styles.footerBtnClickable}
          contentStyle={styles.footerBtnInner}
          accessibilityLabel={t('order.viewOrders')}
          accessibilityRole="button">
          <View style={primary_cta.surface}>
            <Text style={primary_cta.label}>{t('order.viewOrders')}</Text>
          </View>
        </Clickable>

        <Clickable
          onPress={() => navigation.navigate('MainTabs')}
          style={styles.footerBtnClickable}
          contentStyle={styles.footerBtnInner}
          accessibilityLabel={t('order.backToHome')}
          accessibilityRole="button">
          <View style={secondary_cta.surface}>
            <Text style={secondary_cta.label}>{t('order.backToHome')}</Text>
          </View>
        </Clickable>
      </View>
    </StackScreenShell>
  );
};
